// PT catch-up review modal (CATCHUP_WIDGETS_PLAN.md Task 8). Two sections:
// daily-cadence program gaps (a missed day per program, D-CU-16) staged as
// "Did it" (editable time, default noon) / "Didn't" (skipped:true, D-CU-10);
// and per-week-cadence shortfalls ("N of M sessions logged this past week")
// whose "Log a session" action opens a day picker over the past 7 days, since
// the app cannot know which weekdays were intended (D-CU-16). Direct save, no
// pending/confirm, no Bedrock -- POST /catch-up/log. Copies the
// BowelCatchUpModal.tsx shell (staged rows, escape-to-close, local-date->ISO).

import { useState } from "react";
import type { CatchUpLogEntry, PtCatchUpGap, PtWeeklyShortfall } from "@medassist/shared";
import { formatCalendarDate } from "@medassist/shared";
import { logCatchUpEntries, ApiError } from "../lib/api.js";
import { invalidateCatchUp } from "../lib/catchUpFetch.js";
import { ModalShell, CHOICE_CLASS } from "./ModalShell.js";
import { ActivityIcon } from "./icons.js";
import { Select } from "./Select.js";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayStr(): string {
  return localDateStr(new Date());
}

function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDateStr(d);
}

function dayHeading(dayKey: string): string {
  if (dayKey === todayStr()) return "Today";
  if (dayKey === daysAgoStr(1)) return "Yesterday";
  return formatCalendarDate(dayKey, { weekday: "short", month: "short", day: "numeric" });
}

function dateTimeToIso(date: string, time: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const [h, m] = time.split(":").map(Number);
  return new Date(year!, month! - 1, day!, h ?? 12, m ?? 0, 0, 0).toISOString();
}

function to12Hour(hm: string): string {
  const [hStr, mStr] = hm.split(":");
  let h = Number(hStr);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
}

/** Past 7 local days (today back to 6 days ago), newest first -- the weekly
 *  shortfall day picker's candidate set (D-CU-16). */
const PAST_WEEK_DAYS: string[] = Array.from({ length: 7 }, (_, i) => daysAgoStr(i));

interface StagedGap {
  did: boolean;
  time: string; // HH:MM local, default noon (D-CU-9); irrelevant when did=false
}

/** Matches api.ts's catchUpEntryKey for pt entries. */
function entryKeyFor(dayKey: string, programEventId: string): string {
  return `pt|${dayKey}|${programEventId}`;
}

export function PtCatchUpModal({
  patientId,
  gaps,
  weekly,
  onClose,
  onLogged,
}: {
  patientId: string;
  gaps: PtCatchUpGap[];
  weekly: PtWeeklyShortfall[];
  onClose: () => void;
  /** Called after at least one entry saves so the widget can refetch + toast. */
  onLogged: (count: number) => void;
}) {
  // Keyed by entryKey (`pt|dayKey|programEventId`) for both gaps and weekly rows.
  const [staged, setStaged] = useState<Map<string, StagedGap & { dayKey: string; programEventId: string }>>(
    new Map()
  );
  const [editingKey, setEditingKey] = useState<string | null>(null);
  // Weekly rows in the middle of picking a day, before staging.
  const [pickingProgram, setPickingProgram] = useState<string | null>(null);
  const [pickDay, setPickDay] = useState<string>(PAST_WEEK_DAYS[0]!);

  const [settled, setSettled] = useState<Set<string>>(new Set());
  const [skipReasons, setSkipReasons] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visibleGaps = gaps.filter((g) => !settled.has(entryKeyFor(g.dayKey, g.programEventId)));
  // A program never appears in both `gaps` and `weekly` (engine splits on cadence),
  // so matching on the programEventId suffix alone is unambiguous here.
  const visibleWeekly = weekly.filter(
    (w) => ![...settled].some((k) => k.endsWith(`|${w.programEventId}`))
  );

  function stageGap(dayKey: string, programEventId: string, did: boolean) {
    const key = entryKeyFor(dayKey, programEventId);
    setStaged((prev) => new Map(prev).set(key, { did, time: "12:00", dayKey, programEventId }));
    if (did) setEditingKey(key);
  }

  function unstage(key: string) {
    setStaged((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    if (editingKey === key) setEditingKey(null);
  }

  function updateTime(key: string, time: string) {
    setStaged((prev) => {
      const existing = prev.get(key);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(key, { ...existing, time });
      return next;
    });
  }

  function openPicker(programEventId: string) {
    setPickingProgram(programEventId);
    setPickDay(PAST_WEEK_DAYS[0]!);
  }

  function confirmPick(programEventId: string) {
    const key = entryKeyFor(pickDay, programEventId);
    setStaged((prev) => new Map(prev).set(key, { did: true, time: "12:00", dayKey: pickDay, programEventId }));
    setPickingProgram(null);
    setEditingKey(key);
  }

  // A weekly program is "handled" once any staged/settled entry exists for it.
  function weeklyEntryKey(programEventId: string): string | null {
    for (const [key, s] of staged) {
      if (s.programEventId === programEventId) return key;
    }
    for (const key of settled) {
      if (key.endsWith(`|${programEventId}`)) return key;
    }
    return null;
  }

  async function save() {
    if (staged.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const entries: CatchUpLogEntry[] = [...staged.values()].map((s) => ({
        kind: "pt",
        programEventId: s.programEventId,
        dayKey: s.dayKey,
        did: s.did,
        ...(s.did ? { performedAt: dateTimeToIso(s.dayKey, s.time) } : {}),
      }));
      const { saved, skipped } = await logCatchUpEntries(patientId, entries);
      const nowSettled = new Set(settled);
      const reasons = new Map(skipReasons);
      for (const s of saved) {
        nowSettled.add(s.entryKey);
      }
      for (const s of skipped) {
        nowSettled.add(s.entryKey);
        reasons.set(s.entryKey, s.reason);
      }
      setSettled(nowSettled);
      setSkipReasons(reasons);
      setStaged(new Map());
      setEditingKey(null);
      invalidateCatchUp(patientId);
      if (saved.length > 0) onLogged(saved.length);
      if (skipped.length === 0) {
        onClose();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  }

  const nothingLeft = visibleGaps.length === 0 && visibleWeekly.length === 0 && skipReasons.size === 0;

  function renderGap(g: PtCatchUpGap) {
    const key = entryKeyFor(g.dayKey, g.programEventId);
    const action = staged.get(key);
    const isEditing = editingKey === key && action?.did;

    return (
      <li key={key} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <span className="block font-medium text-gray-900 dark:text-gray-100">{dayHeading(g.dayKey)}</span>
            <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{g.programLabel}</span>
          </div>
          {!action && (
            <div className="flex shrink-0 gap-1.5">
              <button onClick={() => stageGap(g.dayKey, g.programEventId, true)} className={CHOICE_CLASS}>
                Did it
              </button>
              <button onClick={() => stageGap(g.dayKey, g.programEventId, false)} className={CHOICE_CLASS}>
                Didn&apos;t
              </button>
            </div>
          )}
        </div>

        {action && !action.did && (
          <div className="mt-1.5 flex items-center gap-2 text-sm">
            <span className="flex-1 text-gray-500 dark:text-gray-400">Marked as skipped</span>
            <button
              onClick={() => unstage(key)}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              Undo
            </button>
          </div>
        )}

        {action?.did && !isEditing && (
          <div className="mt-1.5 flex items-center gap-2 text-sm">
            <span className="flex-1 text-gray-500 dark:text-gray-400">Done at {to12Hour(action.time)}</span>
            <button
              onClick={() => setEditingKey(key)}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-(--accent) hover:bg-(--accent)/10"
            >
              Edit
            </button>
            <button
              onClick={() => unstage(key)}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              Undo
            </button>
          </div>
        )}

        {isEditing && action?.did && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="time"
              value={action.time}
              onChange={(e) => updateTime(key, e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={() => setEditingKey(null)}
              className="rounded-md bg-(--accent) px-2.5 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
            >
              Done
            </button>
          </div>
        )}
      </li>
    );
  }

  function renderWeekly(w: PtWeeklyShortfall) {
    const key = weeklyEntryKey(w.programEventId);
    const action = key ? staged.get(key) : undefined;
    const isEditing = key !== null && editingKey === key && action?.did;
    const isPicking = pickingProgram === w.programEventId;

    return (
      <li key={w.programEventId} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <span className="block font-medium text-gray-900 dark:text-gray-100">{w.programLabel}</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {w.loggedLastWeek} of {w.expectedPerWeek} sessions logged this past week
            </span>
          </div>
          {!action && !isPicking && (
            <button
              onClick={() => openPicker(w.programEventId)}
              className="shrink-0 rounded-full bg-(--accent) px-3 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
            >
              Log a session
            </button>
          )}
        </div>

        {isPicking && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Select
              variant="dense"
              value={pickDay}
              onChange={(e) => setPickDay(e.target.value)}
            >
              {PAST_WEEK_DAYS.map((d) => (
                <option key={d} value={d}>
                  {dayHeading(d)}
                </option>
              ))}
            </Select>
            <button
              onClick={() => confirmPick(w.programEventId)}
              className="rounded-md bg-(--accent) px-2.5 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
            >
              Choose
            </button>
            <button
              onClick={() => setPickingProgram(null)}
              className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>
        )}

        {action?.did && !isEditing && (
          <div className="mt-1.5 flex items-center gap-2 text-sm">
            <span className="flex-1 text-gray-500 dark:text-gray-400">
              Logged {dayHeading(action.dayKey)} at {to12Hour(action.time)}
            </span>
            <button
              onClick={() => setEditingKey(key)}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-(--accent) hover:bg-(--accent)/10"
            >
              Edit
            </button>
            <button
              onClick={() => key && unstage(key)}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              Undo
            </button>
          </div>
        )}

        {isEditing && action?.did && key && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="time"
              value={action.time}
              onChange={(e) => updateTime(key, e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={() => setEditingKey(null)}
              className="rounded-md bg-(--accent) px-2.5 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
            >
              Done
            </button>
          </div>
        )}
      </li>
    );
  }

  return (
    <ModalShell
      title="Catch up on PT"
      icon={<ActivityIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      footer={
        !nothingLeft && (
          <button
            onClick={() => void save()}
            disabled={busy || staged.size === 0}
            className="w-full rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
          >
            {busy ? "Saving…" : `Save ${staged.size} ${staged.size === 1 ? "entry" : "entries"}`}
          </button>
        )
      }
    >
      {error && (
        <p className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </p>
      )}

      {[...skipReasons.entries()].map(([key, reason]) => (
        <p
          key={key}
          className="mb-2 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
        >
          {reason}
        </p>
      ))}

      {nothingLeft ? (
        <p className="py-6 text-center text-sm text-gray-600 dark:text-gray-300">All caught up ✓</p>
      ) : (
        <div className="space-y-3">
          {visibleGaps.length > 0 && <ul className="space-y-2">{visibleGaps.map((g) => renderGap(g))}</ul>}
          {visibleWeekly.length > 0 && <ul className="space-y-2">{visibleWeekly.map((w) => renderWeekly(w))}</ul>}
        </div>
      )}
    </ModalShell>
  );
}
